/**
 * Frontend i18n locale vocabulary (SL-0027, Activity 7). `SUPPORTED_LOCALES` mirrors the backend's
 * own `SUPPORTED_LANGUAGES` (`@console/shared-kernel`, Activity 10) — kept as a separate frontend
 * constant rather than importing the shared-kernel one, since this array also drives
 * `i18next`'s own `supportedLngs` typing and this workspace's `i18next.d.ts` key-typing pipeline,
 * which need a concrete, locally-owned `as const` tuple. Per the "add a new language later"
 * procedure (Resolution 7, Task 10.6), extending support to a third language is code-only: add the
 * new code here, add its namespace JSON files under `src/i18n/locales/<code>/`, and re-run
 * `pnpm verify:i18n-parity` — no migration, no schema change.
 */
export const SUPPORTED_LOCALES = ['it', 'en'] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'it';

/** `localStorage` key the language detector caches the resolved locale under (Task 7.3/12.3). */
export const LOCALE_STORAGE_KEY = 'console.locale';

/**
 * Persisted `localStorage` marker (Task 12.3, review-gate B3), mirroring `theme.ts`'s own
 * `THEME_PENDING_SYNC_KEY`: set immediately before a `PATCH /v1/profile` write-through for
 * `preferredLanguage` is issued, cleared only on confirmed success. While set, `OperatorProvider`'s
 * backend-locale hydration refuses to adopt a differing backend value, and a retry-on-next-load
 * path re-issues the write.
 *
 * code-review (2026-07-15, F4): the marker stores the pending WRITE'S TARGET `AppLocale` (not a
 * bare boolean), mirroring `theme.ts`'s own F4 fix — see that file's `THEME_PENDING_SYNC_KEY` doc
 * comment for the full overlapping-write rationale this key shares symmetrically.
 */
export const LOCALE_PENDING_SYNC_KEY = 'console.locale-pending-sync';

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Mirrors `theme.ts`'s `hasPendingThemeSync`/`markThemeSyncPending`/`clearThemeSyncPending` trio
 * (Task 12.2) — a defensive-read wrapper over the raw `localStorage` marker, kept alongside its
 * own key rather than scattering raw `getItem`/`setItem`/`removeItem` calls across `useLocale.ts`
 * and `OperatorProvider.tsx`. */
export function hasPendingLocaleSync(): boolean {
  try {
    return isAppLocale(localStorage.getItem(LOCALE_PENDING_SYNC_KEY));
  } catch {
    return false;
  }
}

/** Marks `target` as the latest write in flight — overwrites any prior pending target (F4). */
export function markLocaleSyncPending(target: AppLocale): void {
  localStorage.setItem(LOCALE_PENDING_SYNC_KEY, target);
}

/** Clears the marker only when `confirmed` still matches the currently stored pending target
 * (F4) — mirrors `theme.ts`'s `clearThemeSyncPending`. */
export function clearLocaleSyncPending(confirmed: AppLocale): void {
  try {
    if (localStorage.getItem(LOCALE_PENDING_SYNC_KEY) === confirmed) {
      localStorage.removeItem(LOCALE_PENDING_SYNC_KEY);
    }
  } catch {
    // No-op — see doc comment above.
  }
}
